import { showToast } from '../js/uiManager.js';
import { parseApplicationText } from "../js/aiPrefillService.js";

export function initializeModal(appManager: any) {
    let editingApplicationId: string | null = null;

    // Build modal HTML
    const modalHTML = `
        <div class="modal-backdrop"></div>
        <div class="glassmorphic p-6 md:p-8 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto relative z-10">
            <div class="flex justify-between items-center mb-6">
                <h2 id="modalTitle" class="text-2xl font-semibold text-sky-300">Add New Application</h2>
                <button id="closeModalBtn" class="text-slate-400 hover:text-sky-300 transition-colors p-1 rounded-full -mr-2 -mt-2">
                    <i class="ph-x text-2xl"></i>
                </button>
            </div>
            
            <div id="prefillSelectSection" class="mb-6 glassmorphic p-4 rounded-xl shadow-2xl">
                <label for="prefillOption" class="form-label">Choose Input Method</label>
                <select id="prefillOption" class="form-select">
                    <option value="manual">Manual Entry</option>
                    <option value="json">Pre-fill from JSON</option>
                    <option value="ai">Pre-fill with AI</option>
                </select>
            </div>

            <div id="jsonInputSection" class="mb-6 glassmorphic p-4 rounded-xl shadow-2xl" style="display:none;">
                <label for="jsonInputArea" class="form-label">Paste JSON to Pre-fill Form:</label>
                <textarea id="jsonInputArea" rows="5" class="form-textarea" placeholder='e.g., {"companyName": "Acme Corp", ...}'></textarea>
                <button type="button" id="fillFormFromJsonBtn" class="btn btn-info text-sm mt-2">
                    <i class="ph-file-code"></i> Pre-fill Form from JSON
                </button>
                <p class="text-xs text-slate-400 mt-1">Keys: companyName, jobTitle, dateApplied (YYYY-MM-DD), status, jobUrl, notes.</p>
            </div>

            <div id="aiInputSection" class="mb-6 glassmorphic p-4 rounded-xl shadow-2xl" style="display:none;">
                <label for="aiInputArea" class="form-label">Describe the application to Pre-fill Form:</label>
                <textarea id="aiInputArea" rows="5" class="form-textarea" placeholder="e.g., Applied to OpenAI for engineer role on 2025-01-01 with status Interviewing."></textarea>
                <button type="button" id="fillFormFromAiBtn" class="btn btn-info text-sm mt-2">
                    <i class="ph-sparkle"></i> Pre-fill Form with AI
                </button>
            </div>

            <form id="applicationForm" class="space-y-5">
                <input type="hidden" id="applicationId">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label for="companyName" class="form-label flex items-center gap-2"><i class="ph-buildings"></i> Company Name</label>
                        <input type="text" id="companyName" name="companyName" required class="form-input" placeholder="e.g., Google">
                    </div>
                    <div>
                        <label for="jobTitle" class="form-label flex items-center gap-2"><i class="ph-briefcase"></i> Job Title</label>
                        <input type="text" id="jobTitle" name="jobTitle" required class="form-input" placeholder="e.g., Software Engineer">
                    </div>
                    <div>
                        <label for="dateApplied" class="form-label flex items-center gap-2"><i class="ph-calendar-blank"></i> Date Applied</label>
                        <input type="date" id="dateApplied" name="dateApplied" required class="form-input">
                    </div>
                    <div>
                        <label for="status" class="form-label flex items-center gap-2"><i class="ph-list-checks"></i> Status</label>
                        <select id="status" name="status" required class="form-select">
                            <option value="Applied">Applied</option>
                            <option value="Screening">Screening</option>
                            <option value="FollowUp">Followed Up</option>
                            <option value="Interviewing">Interviewing</option>
                            <option value="Thinking">Thinking</option>
                            <option value="Offer">Offer</option>
                            <option value="Rejected">Rejected</option>
                            <option value="Ghosted">Ghosted</option>
                        </select>
                    </div>
                </div>
                <div>
                    <label for="jobUrl" class="form-label flex items-center gap-2"><i class="ph-link"></i> Job Posting URL (Optional)</label>
                    <input type="url" id="jobUrl" name="jobUrl" class="form-input" placeholder="https://example.com/job/123">
                </div>
                <div>
                    <label for="notes" class="form-label flex items-center gap-2"><i class="ph-note"></i> Notes (Optional)</label>
                    <textarea id="notes" name="notes" rows="4" class="form-textarea" placeholder="Contact person, key requirements..."></textarea>
                </div>
                <div class="flex justify-end space-x-3 pt-3">
                    <button type="button" id="cancelModalBtn" class="btn btn-secondary">Cancel</button>
                    <button type="submit" id="saveApplicationBtn" class="btn btn-primary">Save Application</button>
                </div>
            </form>
        </div>
    `;

    const applicationModal = document.getElementById('applicationModal');
    if (!applicationModal) return { openModal: () => {}, closeModal: () => {}, populateForm: () => {} };
    applicationModal.innerHTML = modalHTML;

    const modal = document.getElementById('applicationModal');
    const modalTitle = document.getElementById('modalTitle');
    const saveBtn = document.getElementById('saveApplicationBtn') as HTMLButtonElement | null;
    const form = document.getElementById('applicationForm') as HTMLFormElement | null;
    const prefillSelectSection = document.getElementById('prefillSelectSection') as HTMLElement | null;
    const prefillOption = document.getElementById('prefillOption') as HTMLSelectElement | null;
    const jsonInputSection = document.getElementById('jsonInputSection') as HTMLElement | null;
    const jsonInputArea = document.getElementById('jsonInputArea') as HTMLTextAreaElement | null;
    const aiInputSection = document.getElementById('aiInputSection') as HTMLElement | null;
    const aiInputArea = document.getElementById('aiInputArea') as HTMLTextAreaElement | null;

    const openModal = (isEditing: boolean = false, appId: string | null = null) => {
        editingApplicationId = isEditing ? appId : null;
        if (modalTitle) modalTitle.textContent = isEditing ? 'Edit Application' : 'Add New Application';
        if (saveBtn) saveBtn.textContent = isEditing ? 'Save Changes' : 'Save Application';
        if (form) form.reset();
        const applicationIdInput = document.getElementById('applicationId') as HTMLInputElement | null;
        if (applicationIdInput) applicationIdInput.value = '';
        if (prefillSelectSection) prefillSelectSection.style.display = isEditing ? 'none' : 'block';
        if (prefillOption) prefillOption.value = 'manual';
        if (jsonInputSection) jsonInputSection.style.display = 'none';
        if (jsonInputArea) jsonInputArea.value = '';
        if (aiInputSection) aiInputSection.style.display = 'none';
        if (aiInputArea) aiInputArea.value = '';
        if (modal) {
            modal.classList.remove('hidden');
            (modal as HTMLElement).style.display = 'flex';
            setTimeout(() => modal.classList.add('active'), 10);
        }
    };

    const closeModal = () => {
        if (modal) {
            modal.classList.remove('active');
            setTimeout(() => {
                modal.classList.add('hidden');
                (modal as HTMLElement).style.display = 'none';
            }, 300);
        }
    };

    const populateForm = (app: any) => {
        const companyNameInput = document.getElementById('companyName') as HTMLInputElement | null;
        const jobTitleInput = document.getElementById('jobTitle') as HTMLInputElement | null;
        const dateAppliedInput = document.getElementById('dateApplied') as HTMLInputElement | null;
        const statusInput = document.getElementById('status') as HTMLSelectElement | null;
        const jobUrlInput = document.getElementById('jobUrl') as HTMLInputElement | null;
        const notesInput = document.getElementById('notes') as HTMLTextAreaElement | null;
        if (companyNameInput) companyNameInput.value = app.companyName;
        if (jobTitleInput) jobTitleInput.value = app.jobTitle;
        if (dateAppliedInput) dateAppliedInput.value = app.dateApplied;
        if (statusInput) statusInput.value = app.status;
        if (jobUrlInput) jobUrlInput.value = app.jobUrl || '';
        if (notesInput) notesInput.value = app.notes || '';
    };

    const closeModalBtn = document.getElementById('closeModalBtn');
    if (closeModalBtn) closeModalBtn.addEventListener('click', closeModal);
    const cancelModalBtn = document.getElementById('cancelModalBtn');
    if (cancelModalBtn) cancelModalBtn.addEventListener('click', closeModal);
    if (modal) modal.addEventListener('click', (e) => {
        if (e.target === modal) closeModal();
    });

    if (prefillOption) {
        prefillOption.addEventListener('change', () => {
            if (jsonInputSection) jsonInputSection.style.display = prefillOption.value === 'json' ? 'block' : 'none';
            if (aiInputSection) aiInputSection.style.display = prefillOption.value === 'ai' ? 'block' : 'none';
        });
    }

    const fillFormFromJsonBtn = document.getElementById('fillFormFromJsonBtn');
    if (fillFormFromJsonBtn) fillFormFromJsonBtn.addEventListener('click', () => {
        if (!jsonInputArea) return;
        const jsonString = jsonInputArea.value.trim();
        if (!jsonString) {
            showToast('Please enter JSON data.', 'error');
            return;
        }
        try {
            const jsonData = JSON.parse(jsonString);
            const fieldMap: { [key: string]: string } = {
                companyName: 'companyName',
                jobTitle: 'jobTitle',
                dateApplied: 'dateApplied',
                status: 'status',
                jobUrl: 'jobUrl',
                notes: 'notes'
            };
            Object.keys(fieldMap).forEach(jsonKey => {
                const formField = document.getElementById(fieldMap[jsonKey]) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
                if (formField && jsonData[jsonKey]) {
                    formField.value = jsonData[jsonKey] || '';
                }
            });
            showToast('Form filled from JSON.', 'success');
        } catch (error) {
            showToast('Invalid JSON format.', 'error');
        }
    });

    const fillFormFromAiBtn = document.getElementById('fillFormFromAiBtn') as HTMLButtonElement;
    if (fillFormFromAiBtn) fillFormFromAiBtn.addEventListener('click', async () => {
        if (!aiInputArea) return;
        const userText = aiInputArea.value.trim();
        if (!userText) {
            showToast('Please describe the application.', 'error');
            return;
        }

        const originalButtonContent = fillFormFromAiBtn.innerHTML;
        fillFormFromAiBtn.disabled = true;
        fillFormFromAiBtn.innerHTML = `
            <svg class="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Prefilling...</span>`;

        try {
            const aiData = await parseApplicationText(userText);
            if (!aiData || Object.keys(aiData).length === 0) {
                showToast('AI could not parse the text.', 'error');
                return;
            }
            const fieldMap: { [key: string]: string } = {
                companyName: 'companyName',
                jobTitle: 'jobTitle',
                dateApplied: 'dateApplied',
                status: 'status',
                jobUrl: 'jobUrl',
                notes: 'notes'
            };
            Object.keys(fieldMap).forEach(key => {
                const formField = document.getElementById(fieldMap[key]) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
                const value = (aiData as any)[key];
                if (formField && value) {
                    formField.value = value || '';
                }
            });
            showToast('Form pre-filled with AI.', 'success');
        } catch (error) {
            showToast('AI pre-fill failed. Please try again.', 'error');
        } finally {
            fillFormFromAiBtn.disabled = false;
            fillFormFromAiBtn.innerHTML = originalButtonContent;
        }
    });

    if (form) form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!saveBtn) return;
        saveBtn.disabled = true;
        saveBtn.textContent = editingApplicationId ? 'Saving...' : 'Adding...';

        const formData = new FormData(form);
        const getString = (key: string) => {
            const val = formData.get(key);
            return typeof val === 'string' ? val.trim() : '';
        };
        const applicationData = {
            companyName: getString('companyName'),
            jobTitle: getString('jobTitle'),
            dateApplied: getString('dateApplied'),
            status: getString('status'),
            jobUrl: getString('jobUrl'),
            notes: getString('notes')
        };

        if (!applicationData.companyName || !applicationData.jobTitle || 
            !applicationData.dateApplied || !applicationData.status) {
            showToast('Please fill in all required fields.', 'error');
            saveBtn.disabled = false;
            saveBtn.textContent = editingApplicationId ? 'Save Changes' : 'Save Application';
            return;
        }

        let success;
        if (editingApplicationId) {
            success = await appManager.updateApplication(editingApplicationId, applicationData);
        } else {
            success = await appManager.addApplication(applicationData);
        }

        if (success) {
            editingApplicationId = null;
            closeModal();
            if (form) form.reset();
            if (jsonInputArea) jsonInputArea.value = '';
        }

        saveBtn.disabled = false;
        if (modalTitle && modalTitle.textContent) {
            saveBtn.textContent = modalTitle.textContent.startsWith('Edit') ? 'Save Changes' : 'Save Application';
        }
    });

    return { openModal, closeModal, populateForm };
}